import { Fragment, useEffect, useState } from "react";
import config from "../config";
import { Link } from "react-router-dom";

/**
 * Comprehensive FAQ — a faithful, document-style reproduction of the
 * "GILDer Comprehensive FAQ" (Version 1.1 — April 2026) PDF, backing the
 * public "FAQ" link in the top navigation. Every question, answer, list,
 * table and infographic from the source document is reproduced in order,
 * with the eleven infographics rendered inline at the exact positions
 * they appear in the PDF (extracted to /images/faq/*.png).
 *
 * Content is held in the FAQ_SECTIONS data model below so the layout is
 * driven entirely by data — each section carries its number, title and an
 * ordered list of blocks (standalone images or Q&A entries). A Q&A answer
 * is itself an ordered array of blocks (paragraph string, bullet list,
 * table, or an inline image) so mixed content renders in source order.
 */

const IMG = "/images/faq";

const FAQ_SECTIONS = [
    {
        n: 1,
        title: "General & Overview",
        blocks: [
            {
                q: "What is GILDer?",
                a: [
                    "GILDer is a decentralised protocol that lets you put USDC into a 3-year Term Deposit and earn 20% simple annual interest, accrued daily in USDC which can be withdrawn at any time. 80% of every deposit is locked in a segregated Safe Vault that no one (including the protocol) can touch. The remaining 20% is used to create immediate demand for the GILDer token and to fund the treasury that pays the yield.",
                    "Note: Other protocol products like Turbo or Leverage allow you to utilise your segregated Safe capital and so change the returns, the risk and how much you can access."
                ]
            },
            {
                q: "Is the 20% yield guaranteed?",
                a: [
                    "No yield in DeFi (or anywhere) is 100% guaranteed. However, the yield is paid from real USDC profits generated by the treasury, not from printing new tokens. The protocol is designed to be sustainable even with very low inflows, and it has multiple built-in protections (Safe Vault, penalties, TVG ratchet, graceful shutdown).",
                    "The contract calculates a target return of 20% annually. Payments are made in USDC subject to available protocol reserves. The segregated Safe balance is not a guarantee of future yield."
                ]
            },
            {
                q: "What is the risk?",
                a: [
                    "Covered in more detail later in the FAQ, but the protocol is designed to protect as much of your capital as possible, so even if people stop using the product you should receive most, if not all, of your money back."
                ]
            },
            {
                q: "Why is it 3 years?",
                a: ["The objective of the protocol is to give you a reliable yield.  The yield is generated by selling against buy pressure of the $GILD token.  After running multiple simulations, 3 years was the optimal amount of time to give you the target yield.",
                    "For those that need liquidity early, there is the option to early exit, take leverage, or trade your position once our Marketplace opens.",
                    "In Traditional Finance, an equivalent fixed income product would average just 6-7% and the most common timeframe is 6 years."
                ]
            },
            {
                q: "How is this different from most DeFi yield products?",
                a: [
                    "Most DeFi yield comes from inflation (printing new tokens), high-risk strategies or dependencies on 3rd party performance. GILDer uses no inflation. Yield is paid from real USDC profits created by new deposits and treasury trading. Principal is structurally protected, and the system is designed to get stronger as it grows.  Its two primary 3rd party relationships are USDC & the Base Blockchain."
                ]
            },
            {
                q: "Does this need huge numbers of new users to ‘pump’?",
                a: [
                    "No. The protocol is designed so that it could work successfully if only a small number of people ever used it. However, the more people who do use it, the more sustainable it becomes.",
                    "The focus of the protocol is paying yield, not pumping it’s token."
                ]
            },
            {
                q: "Can I transfer my Term Deposit to a different wallet?",
                a: [
                    "This is coming.  You can transfer ownership of your Term Deposit NFT, but **only through the official Phase 2 Marketplace**. The smart contract uses ERC-721C walled-garden logic that restricts transfers to the authorised marketplace address. Direct transfers outside the marketplace are not possible."
                ]
            },
            {
                q: "What happens if I lose access to my wallet?",
                a: [
                    "Like any DeFi protocol, your assets are tied to your private keys. If you lose your keys, you lose the NFT, and therefore the deposit. We recommend using a hardware wallet for 3-year terms."
                ]
            },
            {
                q: "Where can I find more information?",
                a: [
                    "Everything is on this site.  We have NO Telegram or Discord.  Anyone claiming to be us is fake.  You will find official announcements at [https://x.com/GILDerfinance](https://x.com/GILDerfinance|blank) - That is our only communication channel. And you can direct questions to info@gilderfinance.com but you will probably be ignored or dealt with by an AI bot (sorry)."
                ]
            },

            {
                q: "What is the difference between the 3 products, Standard, Leverage & Turbo?",
                img: { src: `${IMG}/three-ways-to-use-it.png`, alt: "One Product, Three Ways to Use It — Standard, Leverage and Turbo comparison" },
                a: [
                    "GILDer is fundamentally **one product**. Every dollar of USDC that arrives is treated as a Term Deposit (TD) and goes through the same **80 / 10 / 9 / 1 split**:",
                    { list: [
                        "**80%** → Safe Vault (secure, per-user. Does not flow through the engine.)",
                        "**10%** → Token buy on AMM",
                        "**9%** → Tier 1 Yield Treasury",
                        "**1%** → LP top-up"
                    ] },
                    "The engine only ever sees the 20% slice (the 10/9/1). The difference in the 3 products is just what the user chooses to happen to the funds in the secure safe which has an impact on the protocol and the returns it offers.",
                    "Standard. Nothing touches your safe for 3 years.",
                    "Leverage. User can borrow up to 75% of the safe paying a 15% fee p/a",
                    "Turbo. User borrows the maximum possible from the safe and redeploy it into a new Term Deposit as many times as possible (the protocol does this automatically)."
                ]
            },
            {
                q: "Why is it called GILDer?",
                a: [
                    "The name is a direct homage to the Guilder (Gulden), historically recognised as the world’s first truly global trading currency and itself a derivative of the word \"Gold.\"",
                    "In the 17th century, the Dutch Guilder transformed international commerce by providing a borderless, reliable standard of value. It wasn't backed by the promise of a monarch; it was backed by the raw depth of global trade flowing through the Bank of Amsterdam. It laid the groundwork for global capital liquidity because it was universally trusted and highly liquid.",
                    "Today, global trade relies on fiat currencies that are subject to inflation, geopolitical weaponization, and centralised control. Even digital alternatives like fiat-backed stablecoins carry counterparty risk, rely on the promises of centralized custodians, and are pegged to depreciating currencies.",
                    "GILDer harvests volatility when liquidity is thin and harvests volume when liquidity is deep. Oscillating naturally between the two, the protocol functions just as effectively in low-usage scenarios as it does at immense scale. As adoption compounds and traditional finance moves on-chain and into the protocol, this sheer market depth and resilience will naturally subdue the volatility of the core $GILD token. At that point, it begins to behave less like a speculative asset and more like a digital commodity. A digital gold?",
                    "When $GILD achieves this scale-driven stability, deriving its value purely from liquidity and non-sovereignty rather than redemption promises or yield, it invites a compelling macro question: Could a fully transparent, unmanipulated, counterparty-free digital commodity once again serve as a borderless settlement standard for global trade?"
                ]
            }
        ]
    },
    {
        n: 2,
        title: "How the Term Deposit Works",
        blocks: [
            { image: { src: `${IMG}/capital-allocation.png`, alt: "GILDer Protocol: Capital Allocation — 80% Safe Vault, 10% token buy, 9% treasury, 1% liquidity pool" } },
            {
                q: "What happens when I deposit?",
                a: [
                    "Your deposit is split automatically in one transaction:",
                    { list: [
                        "80% → Safe Vault (protected principal)",
                        "10% → Immediate buy of GILDer token on the open market",
                        "9% → Treasury (to pay future yield)",
                        "1% → Liquidity pool reinforcement"
                    ] },
                    "You start earning 20% simple annual interest from day one, accrued daily in USDC."
                ]
            },
            { q: "What is the minimum deposit?", a: ["$100 USDC."] },
            {
                q: "How long is the term?",
                a: ["3 years (1095 days). You can exit early, but you forfeit 20% of principal as a penalty (accrued interest is still paid)."]
            },
            {
                q: "Can I see my deposits and interest?",
                a: [
                    "Yes. Every Term Deposit is represented as a dynamic NFT that shows real-time principal, accrued interest, days to maturity, and Turbo status (if enabled).",
                    "Everything is available to view in the dashboard or onchain."
                ]
            },
            {
                q: "Who holds the keys to the Safe Vault?",
                a: ["No one. The Safe Vault is governed by autonomous smart contracts. The code dictates that funds can only be released to the specific NFT holder upon maturity, loan or via the early-exit function."]
            }
        ]
    },
    {
        n: 3,
        title: "Yield & Economics",
        blocks: [
            {
                q: "Where does the 20% yield actually come from?",
                a: [
                    "From USDC profits generated by the treasury. Every token buy raises the price and stores USDC in the Liquidity Pool. Every new deposit creates 10% immediate buy pressure on the GILDer token. That pressure also pushes the price up. The treasury sells a small slice of tokens at a profit and uses that USDC to pay the yield. The more deposits and token buys that come in, the easier it becomes to generate the yield needed.",
                    "Payments are subject to available protocol reserves."
                ]
            },
            {
                q: "Don’t you need 100% return on the 20% to pay 20% overall?",
                a: ["No. The 10% buy pressure from each deposit creates profit for the treasury. One $100k deposit already covers ~38% of its own first year annual yield obligation. Profit from treasury makes up the rest. Additional deposits make every subsequent yield payment easier because the token price (and therefore profit per token sold) is higher."]
            },
            {
                q: "Does the treasury take “directional” trades (long/short)?",
                img: { src: `${IMG}/tvg-ratchet.png`, alt: "How the TVG Ratchet Works — Treasury sells above TVG, buys below TVG, floor ratchets upward over time" },
                a: ["No. The treasury does not “gamble” on market direction. It exclusively manages the GILDer/USDC liquidity and executes the TVG ratchet strategy (buying low/selling high relative to the floor). This removes the risk of a “bad trade” wiping out the yield reserves."]
            },
            {
                q: "Is the 20% yield fixed or variable?",
                a: [
                    "It is a target rate. While the protocol aims to pay 20% simple interest, it is paid out based on treasury performance. As long as the treasury is healthy the 20% is very achievable. If there are no new users and no token buyers, the protocol will still endeavour to honour its obligations to 20% but might exhaust the protocol of cash before it gets there.",
                    "Your 80% locked in the Safe Vault, plus any interest accrued until that time, is not at risk."
                ]
            },
            {
                q: "How much runway (days of interest coverage) does the protocol have for each new Term Deposit?",
                img: { src: `${IMG}/days-of-interest-coverage.png`, alt: "GILDer Protocol: Days of Interest Coverage — how the 9% buffer supports daily interest payments" },
                a: [
                    "The 9% allocation to the Yield Treasury on every deposit is designed to provide an initial buffer of interest payments while the token buy pressure and TVG ratchet start generating additional USDC profits.",
                    { table: {
                        head: ["Term Deposit Type", "Gross Amount Created", "9% Buffer to Treasury", "Daily Net Payout to User", "Days of Interest Coverage Provided by the 9% Buffer"],
                        rows: [
                            ["Standard Term Deposit", "$10,000", "$900", "$5.48", "~164 days"],
                            ["Turbo Term Deposit", "$25,000", "$2,250", "$7.54", "~298 days"]
                        ]
                    } },
                    "Key points:",
                    { list: [
                        "These are the minimum days of coverage provided by the 9% slice alone for a single deposit.",
                        "Because new deposits are hopefully arriving regularly, the protocol tries to maintain a rolling buffer of 6 months of coverage even in very low-growth scenarios.",
                        "Any trading profits from the TVG ratchet or additional deposits only increase this runway further."
                    ] },
                    "In practice, the protocol is designed to be sustainable with extremely low inflows — it does not require constant large deposits to keep paying yield."
                ]
            },
            {
                q: "How much of the token supply does the treasury need to sell to pay the yield?",
                a: [
                    "Very little once TVL grows. See the chart below (based on standard Term Deposit mechanics):",
                    { image: { src: `${IMG}/tokens-to-sell-chart.png`, alt: "How Many Tokens the Treasury Needs to Sell to Pay the 20% Yield — falls from 0.92% at $100M TVL to 0.09% at $5B TVL" } },
                    "The line falls steeply because each new deposit adds buy pressure and the ratchet effect concentrates value."
                ]
            },
            {
                q: "How does the protocol continue to generate yield when the liquidity pool becomes very deep and new Term Deposits barely move the token price?",
                img: { src: `${IMG}/yield-as-liquidity-deepens.png`, alt: "How GILDer Continues to Generate Yield as Liquidity Deepens — adaptive TVG ratchet mechanism" },
                a: [
                    "The protocol is deliberately designed to adapt as it scales. The TVG ratchet operates in two complementary modes depending on market liquidity:",
                    { list: [
                        "In deep liquidity (the desired long-term state): New deposits have minimal price impact, so the volatility-arbitrage premium shrinks. In this environment the treasury simply sells a small portion of its GILDer tokens directly into the deep liquidity pool at or very near the Treasury Value Guidance (TVG) price. These steady, low-slippage sales convert tokens into the exact USDC required to pay daily yields. No large premium is needed because the LP is already deep enough to absorb the sales efficiently.",
                        "In thinner liquidity (early stages or lower-growth periods): New deposits create meaningful buy pressure that pushes the token price above TVG. The treasury then sells a slice of tokens at the premium, capturing extra USDC profit. This is the classic “volatility harvest” that provides the highest margin."
                    ] },
                    "The protocol is designed that it does not rely on one mode or the other. As the liquidity pool deepens, the mechanism naturally shifts from premium-driven arbitrage to steady, predictable token sales — exactly as intended. The deeper and more liquid the pool becomes, the easier and cheaper it is for the treasury to generate the required USDC.In all cases, the 9% initial buffer, new deposit flow, early-exit penalties, LP fees, and abandoned-account fees continue to support the protocol. The protocol therefore is designed to be sustainable whether it is small or multi-billion in scale."
                ]
            },
            {
                q: "How is Capital Protection Prioritised in the system?",
                img: { src: `${IMG}/capital-protection-waterfall.png`, alt: "Capital Protection Waterfall — Yield Fortress, Floor Defence, Growth & Operations" },
                a: [
                    "The protocol prioritises yield payment over everything else. When the Token is above TVG, the protocol sells and uses a waterfall system to ensure each part of the protocol is funded.",
                    "**1. The Yield Fortress** The protocol first prioritises locking 6 months of upcoming yield payouts in liquid cash. This ensures that even if the market completely stalls, all yield obligations to active bondholders are mathematically covered for half a year.",
                    "**2. The Floor Defence** Once the 6 month yield is secured, the protocol accumulates a dedicated cash shock absorber. This reserve is sized to buy back up to 5 percent of all circulating tokens under certain circumstances. This can provide automated price support during market downturns.",
                    "**3. Growth and Operations** Only when both of these massive safety buffers are 100 percent funded does any surplus profit flow to the deep treasury as back up with 1/6th going to the operations team to cover hosting, security audits, and protocol growth."
                ]
            },
            {
                q: "Can the TVG ever fall?",
                a: ["Yes. It is designed to fall if there is prolonged selling pressure on $GILD, this will drive the price of the token down, the protocol will lower the TVG accordingly and over time will start buying any tokens that are priced below the new TVG."]
            },
            {
                q: "What happens to my account if the Protocol is unable pay the yield?",
                a: [
                    "Once the Protocol has exhausted all possible means of paying yield, including draining all treasuries and selling its tokens to empty the LP, no further yield can be paid.  You can remove your 80% plus whatever interest you did receive at the end of the 3 year term.",
                    "However, in the event that speculators come back into the token, and new investors start buying Term Deposits, the Protocol will endeavour to restore all its outstanding balance to your account assuming your Term Deposit is still live."
                ]
            }
        ]
    },
    {
        n: 4,
        title: "Compounding & Auto-Compound",
        blocks: [
            {
                q: "How does Auto-Compound work?",
                a: ["As soon as your total accrued interest reaches $100, the protocol automatically uses that $100 to open a new 3-year Term Deposit. The more deposits you have, the faster you reach the next $100 threshold, so compounding accelerates over time."]
            },
            {
                q: "What are the long-term numbers with Auto-Compound on a $10,000 deposit?",
                img: { src: `${IMG}/auto-compound-standard.png`, alt: "Auto-Compound Growth Over Time — Consolidated Yield Reinvestment, $10,000 deploy capital (illustrative only)" },
                a: [
                    { table: {
                        head: ["Time Horizon", "Approximate Total Value", "Number of Active Term Deposits", "Effective Annualised Return"],
                        rows: [
                            ["1 year", "≈ $12,169", "22", "~21.7%"],
                            ["3 years", "≈ $18,029", "81", "~21.6%"],
                            ["6 years", "≈ $33,075", "231", "~22.0%"],
                            ["9 years", "≈ $59,723", "498", "~22.4%"],
                            ["12 years", "≈ $109,661", "997", "~22.7%"]
                        ]
                    } }
                ]
            },
            {
                q: "If I use Auto-Compound, does it reset my 3-year timer?",
                a: ["No. Each $100 compound creates a new NFT with its own 3-year timer. Your original deposit still matures on its original schedule."]
            },
            {
                q: "What are the long-term numbers with Consolidated Auto-Compound on a $10,000 Turbo Term Deposit?",
                a: [
                    "Consolidated Auto-Compound pools accrued interest across all of your Term Deposits. The protocol does a sweep each month and accumulates all interest over $100 into a new Term Deposit.",
                    "This dramatically accelerates compounding compared to the per-NFT model.",
                    { image: { src: `${IMG}/auto-compound-turbo.png`, alt: "Auto-Compound Growth Over Time — Turbo Term Deposit, $10,000 deploy capital (illustrative only)" } },
                    "Numbers are approximate for illustration only and will vary according to multiple factors",
                    { table: {
                        head: ["Time Horizon", "Approximate Total Value", "Number of Active Term Deposits", "Effective Annualised Return"],
                        rows: [
                            ["1 year", "≈ $13,100", "32", "~31.0%"],
                            ["3 years", "≈ $22,700", "128", "~31.0%"],
                            ["6 years", "≈ $48,000", "~400", "~31.0%"],
                            ["9 years", "≈ $100,000+", "~800", "~31.0%"],
                            ["12 years", "≈ $210,000+", "~1,600", "~31.0%"]
                        ]
                    } }
                ]
            }
        ]
    },
    {
        n: 5,
        title: "Turbo Loop & Leverage",
        blocks: [
            {
                q: "Can I Borrow Money Against My Deposit?",
                a: [
                    "Yes with a Standard Term Deposit. 80% of your capital is stored in a ‘safe’ that only you have access to. If you need cash, rather than cancelling the Term Deposit, you can borrow up to 75% of the USDC from this safe for a flat 15% p/a fee.",
                    "This is only available on the Standard Term Deposit, the Turbo Loop is already using that capital so no leverage is available.",
                    "The 15% you owe is offset against the 20% you are earning on the full amount."
                ]
            },
            {
                q: "Do I Still Earn 20% On My Principal?",
                a: [
                    "Yes. You will continue to earn 20% on the full amount deposited and you will pay 15% on however much you borrow. In effect, your interest pays down your debt.",
                    "Example, if you deposit $10,000 you will earn 20% p/a ($2,000). If you then immediately withdraw the maximum amount (75% of $8,000 is $6,000), you will be paying 15% p/a on the $6,000.",
                    "Overall you will still receive 11% p/a ($1,100) on the full value of $10,000 from the protocol, whilst being able to use the $6,000 for your immediate needs."
                ]
            },
            {
                q: "Do I Need To Pay Back My Loan?",
                a: [
                    "No. If you do not pay back your loan, there is no penalty. At the end of the Term Deposit, there will just be less money in your Safe to access.",
                    "**Note:** If you do not withdraw your funds at the end of 3 years, you will still be paying the 15% on the loan, but no longer receiving the 20% on the Principal. This could lead to liquidating your account, so do make sure to exit your position at the end of the 3 years or reinvest in a new Term Deposit."
                ]
            },
            {
                q: "What is Turbo Loop?",
                img: { src: `${IMG}/turbo-loop.png`, alt: "How Turbo Loop Works — automated execution facility, recursive collateralisation, up to ~2.5× gross exposure" },
                a: [
                    "One-click leveraged deposit. You deposit, borrow against your Safe Vault, re-deposit, and repeat up to approximately 2.5×. Net yield becomes up to 27.5% on your equity. It also creates immediate 2.5× token buy pressure, which is very healthy for the protocol.",
                    "However, if you choose to exit before the 3 years the penalty is much higher because the capital in your Safe has been used multiple times. You will only receive 50% of your initial capital plus whatever you have earned so far in interest.",
                    "To get the full 27.5% you need to deploy over $10,000 because of the looping functionality. The lowest you will receive is 11% on $100"
                ]
            },
            {
                q: "Is Turbo safe?",
                a: ["There is no price-based liquidation (everything is USDC vs USDC). The only real risk is a much higher early-exit penalty if you exit before maturity."]
            }
        ]
    },
    {
        n: 6,
        title: "Liquidity & Exiting",
        blocks: [
            { image: { src: `${IMG}/four-ways-out.png`, alt: "four Ways Out — wait until maturity, exit early, or Phase 2 Marketplace" } },
            {
                q: "Can I get my money out early?",
                a: [
                    "Yes. You forfeit 20% of principal as a penalty, but you keep all accrued interest. The penalty goes to the treasury, strengthening reserves.",
                    "Alternatively, you can borrow against your funds in the Safe."
                ]
            },
            {
                q: "How do I get liquidity without the penalty?",
                a: [
                    "Phase 2 Marketplace lets you trade your Term Deposit NFT to another user. This is the intended way to exit early without paying the 20% penalty. How much you can get back will depend on what buyers are prepared to offer and what discount you are willing to accept.",
                    "There is also the Leverage product which allows you to borrow upto 75% of your Safe money at an annual fee of 15%"
                ]
            }
        ]
    },
    {
        n: 7,
        title: "Token, TVG & Legal",
        blocks: [
            { image: { src: `${IMG}/token-tvg-and-legal.png`, alt: "Token, TVG & Legal" } },
            {
                q: "What is TVG?",
                a: [
                    "Treasury Value per GILDer = total treasury USDC ÷ circulating supply. It is the fundamental floor value of the token. The treasury buys below TVG and sells above TVG, creating a ratchet that makes the floor rise over time.",
                    "**Numerator:** USDC in the three reserve buckets that can be deployed to defend the token. Safe Vault is NOT included because its user principal, locked.",
                    "**Denominator:** GILDer tokens held by the public. Treasury inventory does NOT count. Tokens bought back by the protocol are not included in circulating supply."
                ]
            },
            {
                q: "Why would anyone buy the token?",
                a: ["Because every new deposit creates buy pressure, and the ratchet pushes value higher as the protocol grows. In the early years of the protocol the expected growth of the token is expected to be significant, as the protocol matures its growth will also stabilise."]
            },
            {
                q: "Are there any founder, team, or VC tokens that could be sold or dumped on the market?",
                a: [
                    "No. There are no founder, team, or VC token allocations whatsoever. All 1 trillion GILDer tokens were allocated at genesis to protocol-controlled wallets only:",
                    { list: [
                        "50% Treasury",
                        "30% Yield Treasury",
                        "15% Marketing & Operations wallet",
                        "5% Liquidity Pool"
                    ] },
                    "Unless purchased on the open market after launch, no private wallets hold any tokens outside the protocol’s own smart-contract-controlled allocations."
                ]
            },
            {
                q: "Why did the founders not allocate any tokens to themselves or take on external investors?",
                a: ["The founders built the product that they wanted to use and did not need to extract capital from the market.  The tokenomics are designed to be the most elegantly efficient means of providing high yield to investors whether they have $100 or $1m looking for returns. You can read more in their [manifesto](/manifesto|blank)"]
            },
            {
                q: "Can the Marketing/Ops wallet sell tokens, and does the team ever extract profit from the protocol?",
                a: [
                    "No. The Marketing/Ops wallet (15% of total supply) is fully protocol-controlled and exists solely to fund growth, development, audits, partnerships, hosting, operational costs and the GILDerFoundation (charitable givings).",
                    "Any USDC sent to the Marketing/Ops wallet comes exclusively from:",
                    { list: [
                        "LP trading fees (0.3%)",
                        "The Marketing Refill (16% of surplus from premium-zone sales)",
                        "Fees from abandoned Term Deposits (5% monthly in the Decay Phase)",
                        "Trading fees from the Marketplace in phase 2"
                    ] },
                    "How the funds are allocated is determined by the team based on the needs of the protocol, however the protocol is designed so that it is not dependent on any funds that might go to the Marketing/Ops wallet.",
                    "There is **no profit extraction mechanism** built into the protocol for founders or the team. The protocol is deliberately designed so that the more successful it becomes, the stronger it becomes for every user.",
                    "Any value that flows to the Marketing/Ops wallet is used only to grow the protocol and pay associated costs. Funds are never extracted that would impact the protocol at the expense of users.",
                    "All activity involving funding the Marketing/Ops wallet is governed by strict on-chain rules, including solvency locks that suspend outflows if the Interest Reserve falls below safe levels."
                ]
            },
            {
                q: "Is the GILDer token a security?",
                a: [
                    "We don’t believe so. The GILDer token is structured as a protocol utility and input commodity rather than an investment contract.",
                    "While speculators might choose to spend USDC to acquire tokens, the protocol explicitly sells against holders when the price is above TVG and only buys when the price is below TVG.",
                    "There is no dividend, no yield paid to token holders, and no governance rights over protocol profits. The token exists as a utility to serve our yield clients.",
                    "The documentation is clear that the Treasury’s actions are to prioritise yield and so are designed to suppress upside above TVG while providing a floor below it. In our opinion this design directly contradicts the “expectation of profits from the efforts of others” required under the Howey test in the United States and similar criteria under MiCA in Europe.",
                    "However, the regulatory treatment of $GILD and the GILDer Term Deposit may vary by jurisdiction and may change over time. The protocol is obtaining specialist legal advice and does not represent that any product falls outside securities, financial services or crypto-asset regulation. Access may be restricted where required."
                ]
            },
            {
                q: "Why would a rational person buy the GILDer token if the protocol is hard coded to sell against them?",
                a: [
                    "Token buyers are effectively market participants rather than passive investors.",
                    "Common reasons include providing liquidity, front-running anticipated treasury buybacks when the price falls below TVG, or simply wanting exposure to the growth of the underlying Term Deposit product.",
                    "The token does not promise or deliver yield or revenue share to holders, its value is driven by protocol activity and market dynamics, similar to how traders buy and sell commodities like oil or gold."
                ]
            },
            {
                q: "Does the protocol promise token price appreciation or growth?",
                a: [
                    "No. The documentation explicitly states that the Treasury will sell GILDer tokens when the market price exceeds TVG.",
                    "This mechanism caps excessive upside and harvests volatility for the benefit of the bondholders, not token holders.",
                    "There are no marketing promises, roadmaps, or implicit commitments to drive token price higher. Any appreciation is a secondary market outcome, not a protocol objective."
                ]
            },
            {
                q: "How does the GILDer token differ from most other DeFi tokens?",
                a: [
                    "Most DeFi tokens are designed to benefit holders through emissions, revenue share, or governance rights that create an expectation of profit. The core objective of the founders is to drive the price of the token so that early participants can exit at a profit.",
                    "GILDer does the opposite: the token is deliberately structured so the protocol itself is the primary seller at premiums. This removes the “common enterprise with expectation of profits” element that causes many tokens to fail securities law tests. It functions more like protocol fuel or collateral than equity."
                ]
            },
            {
                q: "What about regulation in Europe (MiCA)?",
                a: ["Under MiCA, we believe the GILDer token does not qualify as an asset-referenced token or e-money token. It grants no entitlement to yield, revenue, or redemption rights, and it is not marketed as an investment. It is best classified as a utility token tied to protocol activity. While full decentralisation strengthens the position, the token itself already fails the core criteria that would bring it under stricter MiCA rules."]
            },
            {
                q: "Are there any founder, team, or VC tokens that could create legal risk?",
                a: ["No. All tokens were allocated at genesis to protocol-controlled wallets only. There are no founder, team, or VC allocations whatsoever. This removes one of the most common red flags in token legal analysis."]
            },
            {
                q: "Does anything on the website constitute investment advice?",
                a: ["No. Usual caveats apply, never risk more capital than you are willing to lose, do your own research, see Risks & Safety below."]
            }
        ]
    },
    {
        n: 8,
        title: "Risks & Safety",
        blocks: [
            { image: { src: `${IMG}/safety.png`, alt: "Safety" } },
            {
                q: "Is my principal safe?",
                a: ["80% is in a structurally segregated Safe Vault that the treasury, trading logic, and even emergency controls cannot access. Only your own Term Deposit contract can move it at maturity or early exit. However, if you use Turbo for higher returns, or Leverage to access your capital this changes what can be accessed if you choose to exit early."]
            },
            { image: { src: `${IMG}/risks.png`, alt: "Risks" } },
            {
                q: "What are the biggest risks?",
                a: [
                    { list: [
                        "Smart-contract risk (mitigated by external security contractors and bug bounties (coming soon))",
                        "Regulatory risk (we use offshore structures and geofencing)",
                        "Yield sustainability if growth stops (the protocol is designed to survive on very low inflows and even benefit from early exits)",
                        "New AI tools are finding new ways to hack protocols, however the most common hacks still remain social engineering to attack your own wallet."
                    ] }
                ]
            },
            {
                q: "Why is the frontend geo-fenced?",
                a: [
                    "We have geo-fenced the website and app to block access from certain high-risk jurisdictions (including the United States and Singapore). This is a temporary compliance measure while the protocol transitions to its full decentralised structure (Marshall Islands DAO LLC + Cayman foundation). It helps reduce regulatory exposure during the highest-risk launch period. The underlying smart contracts remain fully permissionless and decentralised. Anyone can still interact directly with the protocol using a wallet and a block explorer, regardless of location. The geo-fence only affects the convenient web interface. We may remove or relax the geo-fencing once the legal position in those countries has been clarified.",
                    "Whilst the frontend may restrict access from certain jurisdictions. Users remain responsible for complying with all laws applicable to them, and restricted persons must not access the protocol through alternative interfaces."
                ]
            },
            {
                q: "What are the protocol dependencies & risks?",
                a: [
                    "This protocol was designed with the minimum amount of external dependencies to ensure maximum client security. The two primary technical dependencies are:",
                    "**Base Protocol:** [https://www.base.org/](https://www.base.org/|blank)",
                    "**USDC:** [0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913](https://basescan.org/token/0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913|blank) .",
                    "In the event that USDC does depeg, the protocol will freeze new Term Deposits. GILDer obligations are denominated in USDC, not guaranteed US dollars. A user may receive the contracted number of USDC while those tokens are worth less than one dollar. That is beyond the control of the protocol.",
                    "Other dependencies might include:",
                    { list: [
                        "Chainlink price feeds",
                        "The selected AMM or DEX",
                        "Keeper execution",
                        "MEV-protected routing",
                        "Wallet infrastructure",
                        "Frontend and indexing",
                        "Marketplace contracts in Phase 2"
                    ] }
                ]
            },
            {
                q: "Has anything like this been done before?",
                a: ["Not with this combination of principal protection, no token inflation, and the TVG ratchet. Previous attempts to pay high yield on Stable coins have proven to be unsustainable (both the yields and the stablecoins), for multiple different reasons we have tried to mitigate in our design."]
            },
            {
                q: "Why hasn’t a product like this existed before?",
                a: [
                    "On the surface, GILDer looks simple: high sustainable yield on USDC with strong principal protection.",
                    "In reality, creating this combination has proven extremely complicated, which is why nothing exactly like it has been built before. Most DeFi yield products were never designed for long-term sustainability. They rely on token inflation, emissions, or hidden leverage to attract capital quickly and eventually collapse when those incentives ran out.",
                    "Building a truly closed-loop system that pays USDC yield without printing tokens requires a very specific and tightly coupled set of mechanics: deterministic 80/20 capital routing, a structurally segregated Safe Vault, the TVG ratchet, self-paying loans, and a graceful shutdown backstop. All of this with founders who funded development choosing not to extract capital from the protocol.",
                    "Getting all of this to work together reliably is technically complex, carries genuine regulatory risk and is expensive to maintain. Additionally, to make the protocol most robust, the design is deliberately founder-unfriendly: no founder/VC token allocations, no profit extraction, and a strong emphasis on principal protection over rapid hype.",
                    "Most teams optimise for fast token launches and personal upside rather than long-term protocol health. GILDer exists because it is willing to accept slower initial growth, higher technical and regulatory risk, and an economic model to build something that can actually last.",
                    "The result is a product that should be simple for users but was exceedingly complex to design & build correctly."
                ]
            },
            {
                q: "What happens if no one makes new deposits for a month?",
                a: [
                    "The protocol doesn't \"break.\" Since 80% is in the Safe Vault, your principal remains secure. The yield might eventually slow down if treasury profits are thin, but the design is such the protocol will sell every token it owns to capture any USDC to pay it’s yield obligations.",
                    "There is full transparency so you can always see the health status of the protocol."
                ]
            },
            {
                q: "Is there a \"Graceful Shutdown\" mode?",
                a: ["Yes. If the protocol ever reaches a point where it is no longer sustainable to continue (no new users, no token buyers), a \"Graceful Shutdown\" is automatically triggered. In this scenario, the protocol drains all remaining USDC in the contracts and LP’s, and remaining treasury assets are distributed proportionally to Term Deposit holders."]
            }
        ]
    },
    {
        n: 9,
        title: "Standard, Leverage and Turbo",
        blocks: [
            {
                q: "Can I convert a Standard Term Deposit to Leverage after it has been opened?",
                a: ["Yes. You can enable Leverage on an existing Standard Term Deposit at any time before maturity. The 80% Safe Vault balance becomes available for borrowing up to 75% at the standard 15% p/a fee."]
            },
            {
                q: "Can I convert a Standard or Leverage Term Deposit to Turbo later?",
                a: ["No. If you wish to have Turbo Loop, you need to initiate that when you first deploy."]
            },
            {
                q: "Can a Leverage user borrow in multiple transactions?",
                a: ["Yes. You can borrow against your Safe Vault in multiple transactions up to the maximum 75% LTV limit. You only pay 15% on the amount you borrow and should you repay it early, that interest charge is removed."]
            },
            {
                q: "Can Leverage debt be partially repaid?",
                a: ["Yes. You can repay any portion of the outstanding Leverage debt at any time before maturity."]
            },
            {
                q: "How is the annual Leverage charge calculated and netted?",
                a: ["You continue to earn 20% on your full original principal. You pay 15% p/a only on the amount you have borrowed. The two amounts are netted, so a user who borrows the maximum 75% of their Safe Vault effectively receives a net 11% p/a on the full deposit value."]
            },
            {
                q: "What exactly is returned at maturity under Standard, maximum Leverage, and Turbo?",
                a: [
                    "At maturity you receive your everything remaining in your Safe Vault plus the 20% in the Protocol and all accrued interest.",
                    { list: [
                        "Standard: You receive 100% of your original principal + accrued interest.",
                        "Maximum Leverage: You receive your original principal minus any outstanding debt + accrued interest.",
                        "Turbo: You receive 100% of your original principal + accrued interest."
                    ] }
                ]
            },
            {
                q: "Under what circumstances can internal liquidation occur?",
                a: ["Internal liquidation can only occur if you fail to manage or exit a Leverage position after maturity. If you do not withdraw or roll over your position at the end of the 3-year term, the 15% Leverage fee will continue to be charged while the 20% yield stops, which can eventually erode the Safe Vault balance triggering liquidation"]
            },
            {
                q: "What happens to outstanding Leverage debt if the NFT is sold on the Marketplace?",
                a: ["The NFT includes everything that is owned and owed. When it is transferred all obligations go with it."]
            },
            {
                q: "Who receives yield accrued before an NFT is transferred?",
                a: ["Accrued yield up to the point of transfer belongs to the original owner. Any yield accruing after the transfer belongs to the new owner."]
            },
            {
                q: "What is the precise early-exit calculation for each configuration?",
                a: [
                    { list: [
                        "Standard: You forfeit 20% of principal. All accrued interest is paid.",
                        "Leverage: You forfeit 20% of principal. Outstanding debt is deducted from the remaining Safe balance. Accrued interest is paid.",
                        "Turbo: You receive everything remaining in the Safe Vault (approximately 50% of the original gross position) plus any interest earned to date."
                    ] }
                ]
            }
        ]
    },
    {
        n: 10,
        title: "Protocol Solvency & Shortfalls",
        blocks: [
            {
                q: "What happens if the Yield Treasury cannot make a scheduled payment?",
                a: ["The protocol will continue attempting to pay yield from available reserves. In extreme prolonged shortfalls, payments may be delayed until new deposits, early-exit penalties, or treasury trading profits restore liquidity."]
            },
            {
                q: "Are missed yields accrued as liabilities, reduced proportionally, or permanently lost?",
                a: ["They would be accrued as liabilities and once the protocol returned to health would be honoured if the account is still active."]
            },
            {
                q: "What is paid first during a shortfall: matured principal, current yield, accrued yield or early exits?",
                a: ["Each account is treated separately. Priority is always given to paying yield obligations to active bondholders."]
            },
            { 
                q: "Where can users see total liabilities, not just assets?", 
                a: ["In the [dashboard](/dashboard|blank)."] 
            },
            {
                q: "What exactly counts as TVL or AUM?",
                a: [
                    "TVL is the total principal held across every active Term Deposit — both the segregated Safe Vault portion and the working capital that runs through the engine. It counts principal only: accrued interest and the treasury's own reserves are tracked separately, and the liquidity pool is not included.",
                    "The live figure is shown on the Protocol Health page and updates as deposits open, mature or exit."
                ]
            },
            {
                q: "What is the current number of days of forward yield coverage?",
                a: ["This figure is visible in the Protocol Health page and updates in real time based on current treasury reserves and daily payout obligations."]
            },
            {
                q: "Can deposits be automatically capped when LP depth or reserve capacity is insufficient?",
                a: ["The protocol does not currently auto-cap deposits, but very large deposits in thin liquidity conditions may experience higher slippage on the 10% token purchase."]
            }
        ]
    },
    {
        n: 11,
        title: "Maturity and Inactivity",
        blocks: [
            { image: { src: `${IMG}/maturity-and-inactivity.png`, alt: "Maturity and Inactivity" } },
            {
                q: "What happens automatically at the end of three years?",
                a: ["Your Term Deposit reaches maturity. You stop earning the 20% yield. You can then withdraw your full Principal including the Safe Vault balance plus accrued interest."]
            },
            {
                q: "Does interest stop immediately at maturity?",
                a: ["Yes. The 20% yield accrual stops at the exact moment of maturity."]
            },
            {
                q: "How long does the owner have to claim or roll over the position?",
                a: ["There is a 12-month grace period after maturity during which you can withdraw or roll over without additional charges."]
            },
            {
                q: "Does the 12-month grace period and subsequent 5% monthly maintenance charge still apply?",
                a: ["Yes. After the 12-month grace period, a 5% monthly maintenance fee begins to be charged on the remaining Safe Vault balance for inactive or abandoned positions."]
            }
        ]
    },
    {
        n: 12,
        title: "Yield and Treasury Mechanics (Additional)",
        blocks: [
            {
                q: "Can the target yield rate ever be changed for new deposits?",
                a: ["The target rate can be adjusted by governance for new deposits only. Existing deposits continue at the rate that was active when they were created."]
            },
            {
                q: "Can the target yield rate be changed for existing deposits?",
                a: ["No. The target rate is locked for the life of each Term Deposit but is always dependent on the health of the treasury."]
            },
            {
                q: "What happens if there are no deposits and no independent token buyers for three years?",
                a: ["The protocol is designed to continue operating using early-exit penalties, LP fees, and abandoned account fees. Yield payments would slow significantly but the Safe Vault principal remains protected."]
            },
            {
                q: "How much Treasury token inventory remains, and how is depletion reported?",
                a: ["All details about the protocol health can be viewed on the the Protocol Health page."]
            },
            {
                q: "Can the Treasury sell enough token inventory to affect price materially?",
                a: ["Yes. It is designed to do so. The yield is paid through the protocol selling against token buys and returning the price down to TVG."]
            },
            {
                q: "Does a Treasury sale change circulating supply?",
                a: ["Yes. Circulating Supply = Total GILDer tokens held outside of protocol-controlled wallets (i.e. public wallets + AMM liquidity pools). Tokens held in the Treasury, Yield Treasury, Operations wallet, or other protocol-controlled addresses are excluded."]
            },
            {
                q: "Can TVG fall, and under what conditions?",
                a: ["Yes. Prolonged selling pressure that drives the market price below the current TVG will cause the protocol to lower TVG over time and begin buying tokens."]
            },
            {
                q: "Does a token holder have any right to redeem at TVG?",
                a: ["No. TVG is a guidance floor used by the treasury for its own buying and selling. Token holders have no direct redemption right at TVG."]
            },
            {
                q: "How are oracle manipulation, front-running and MEV handled?",
                a: ["The protocol uses MEV-protected routing where available and relies on Chainlink price feeds with standard safeguards."]
            },
            {
                q: "What happens when the AMM lacks sufficient liquidity to complete the 10% purchase?",
                a: ["The deposit will still succeed, but the token purchase portion may experience higher slippage or be partially delayed until liquidity improves."]
            }
        ]
    },
    {
        n: 13,
        title: "Governance and Security",
        blocks: [
            {
                q: "Which contracts are immutable and which are upgradeable?",
                a: [
                    "The rules that decide what happens to your money are fixed in the contract code and cannot be edited. The 80 / 10 / 9 / 1 split, the 20% simple rate, the 3-year term, the early-exit penalties, the $100 minimum, the auto-compound threshold and the reserve waterfall are all hard-coded constants — they behave the same for every deposit, forever.",
                    "Around that fixed core, the operational modules sit behind a standard EIP-1967 (UUPS) proxy so a genuine bug can be patched without redeploying the whole protocol. But nothing can be swapped out quietly: any upgrade has to be proposed on the governance multisig and clear its timelock before it takes effect, and the long-term plan is to burn the upgrade keys after the first full 5-year cycle, at which point even that path is closed. The Safe Vault, the capital routing and the yield calculation are part of the immutable core."
                ]
            },
            {
                q: "Who are the multisig signers, what is the threshold and are they independent?",
                a: [
                    "Governance runs through an on-chain multisig with a built-in timelock. It holds every privileged role (admin, parameter and pauser) as well as the proxy-admin slot, so every sensitive action follows the same path: one signer proposes it, the required number of signers confirm it, the timelock passes, and only then can it run.",
                    "This means no single key can change a setting, pause anything, or push an upgrade on its own — and even the signer list, the threshold and the delay can only be changed by going back through the multisig itself. The exact signers, the m-of-n threshold and the delay are set at deployment and published alongside the verified contract addresses so anyone can check them on-chain."
                ]
            },
            {
                q: "Which parameters can governance change?",
                a: [
                    "Governance can only adjust operational settings — never the core economics. Working through the multisig and its timelock, it can:",
                    { list: [
                        "Pause or re-enable new Term Deposits.",
                        "Tune the lending risk settings: maximum borrow LTV, the liquidation threshold, and the loan interest rate.",
                        "Adjust treasury settings such as the solvency floor and the 6-month yield-coverage target.",
                        "Re-point a module address if a component is upgraded (treasury, Safe Vault, lending, Turbo, reinvestment, the liquidity and token-buy routers, the marketing wallet, and so on).",
                        "Manage NFT admin, like the marketplace transfer whitelist and the metadata URL."
                    ] },
                    "What it cannot touch is the part that protects you: the 80 / 10 / 9 / 1 routing, the 20% rate, the 3-year term, the early-exit penalties, the $100 minimum and the reserve waterfall are all constants and sit outside governance entirely."
                ]
            },
            {
                q: "What timelocks apply?",
                a: [
                    "Every governance action is timelocked. When a change is proposed on the multisig it is stamped with an earliest-execution time; it cannot run until that delay has elapsed and enough signers have confirmed. The same applies to parameter changes and to proxy upgrades.",
                    "The point is transparency: the delay gives everyone a public, on-chain window to see a pending change before it lands. The exact delay is set at deployment, is visible on-chain, and can itself only be changed by going through the multisig."
                ]
            },
            {
                q: "What can the emergency controls pause?",
                a: [
                    "There is a single emergency brake that halts the protocol: new Term Deposits, treasury trading (the skim, harvest and buyback operations), the token-buy and market functions, lending and Turbo, and — while the brake is engaged — settlements, interest withdrawals and early exits as well.",
                    "Withdrawals are included on purpose. If an attacker is draining the protocol they do so through paths that look like ordinary withdrawals, so a brake that left those open would not stop the drain it exists for. The brake is a halt, not a seizure: it freezes state exactly as it is and moves nothing. Your principal and accrued interest stay recorded against your deposit, and interest keeps accruing across the freeze, so you are not out of pocket for the outage.",
                    "The brake is deliberately hard to engage and harder to release. Any single authorised signer can freeze the protocol immediately, with no timelock — hesitation is the expensive mistake during an attack. Releasing it requires EVERY multisig signer to confirm, so no single key, including a compromised admin or deploy key, can restart the protocol alone."
                ]
            },
            {
                q: "Can withdrawals ever be paused?",
                a: [
                    "Yes — but only by the emergency brake, and only while the protocol is frozen because it is under attack. Nothing else pauses withdrawals: a USDC de-peg or a governance pause on new deposits leaves withdrawals, maturity claims and early exits working normally.",
                    "A freeze does not move or take custody of anything. It stops state from changing while the incident is dealt with; your balance is untouched and interest continues to accrue. Lifting it requires a unanimous confirmation from every multisig signer."
                ]
            },
            { q: "What exact audit scope has been completed?", a: ["Coming soon"] },
            { q: "Where are the audit reports and verified contract addresses?", a: ["Audit reports will be published on the official site once finalised."] },
            {
                q: "What happens if Base stops producing blocks?",
                a: ["All on-chain activity would pause until the Base network resumes. User funds in the Safe Vault remain safe as they are controlled by immutable logic."]
            },
            {
                q: "What happens if Chainlink stops updating?",
                a: ["Treasury trading and certain automated functions would pause until price feeds resume."]
            },
            {
                q: "What happens if Circle freezes a GILDer contract address?",
                a: ["New deposits would be blocked. Existing positions would continue to function but could not receive or send USDC until the freeze is lifted."]
            },
            { q: "How are private keys, keepers and Treasury execution secured?", a: ["Coming soon"] }
        ]
    },
    {
        n: 14,
        title: "Graceful Shutdown",
        blocks: [
            { image: { src: `${IMG}/graceful-shutdown.png`, alt: "Graceful Shutdown" } },
            {
                q: "What triggers shutdown?",
                a: ["Graceful shutdown is automatically triggered if the protocol reaches a state where it can no longer sustainably meet its obligations (for example, after prolonged zero inflows and depleted reserves)."]
            },
            { q: "Is the trigger automatic or controlled by governance?", a: ["The trigger is automatic based on on-chain conditions."] },
            {
                q: "What happens to each user’s Safe balance?",
                a: ["Every user’s segregated Safe Vault balance remains fully protected and can be extracted by the user at any time."]
            },
            {
                q: "How are Tier 1, Tier 2, Deep Treasury and LP assets distributed?",
                a: ["All remaining assets are used to meet outstanding obligations. Each user account is treated separately and will continue to draw from available treasury reserves until exhausted."]
            },
            {
                q: "How are Standard, Leverage and Turbo claims treated differently?",
                a: ["Each position is handled according to its own mechanics. Leverage debt is deducted from the Safe balance. Turbo positions receive whatever remains in the Safe Vault after internal chain accounting."]
            },
            {
                q: "Are token holders entitled to anything during shutdown?",
                a: ["No. Token holders have no claim on protocol assets during graceful shutdown."]
            },
            {
                q: "Are accrued but unpaid yields senior or subordinate to principal?",
                a: ["Yield obligations are prioritised. The protocol will attempt to pay accrued yield before returning principal where possible."]
            },
            { image: { src: `${IMG}/protocol-roadmap.png`, alt: "Protocol Roadmap" } }

        ]
    },
    {
        n: 15,
        title: "Phase 2: Marketplace & NFT Trading",
        blocks: [
            {
                q: "What is the Phase 2 Marketplace?",
                a: ["It is a closed, protocol-owned marketplace where you can trade your Term Deposit NFT to another user. This gives you liquidity without paying the 20% early-exit penalty."]
            },
            {
                q: "How does the 2% royalty work?",
                a: ["Every secondary sale routes through the authorised marketplace contract. 2% of the sale price is automatically sent to the protocol."]
            }
        ]
    },
    {
        n: 16,
        title: "Phase 3: Debit Card",
        blocks: [
            {
                q: "What is the GILD debit card?",
                a: ["Once launched (Phase 3), it will let you spend the daily interest you earn directly from your Term Deposit (or Turbo) balance. It turns your yield into everyday spending money without breaking the deposit."]
            }
        ]
    },
    {
        n: 17,
        title: "Governance & DAO Transition",
        blocks: [
            {
                q: "Who controls the protocol today?",
                a: ["In Year 1 a small multisig controls only non-critical parameters (deposit pause, emergency functions). Core economic rules (Safe Vault, routing, yield calculation) are immutable."]
            },
            {
                q: "When will it become a full DAO?",
                a: ["The plan is to transition to a Marshall Islands DAO LLC + Cayman foundation structure within the first 1–2 years, once the protocol has proven itself and generates enough revenue to cover legal and operational costs. The ultimate goal is full key burn after the first complete 5-year cycle (3 years bonds + 1 year hold + 1 year drain)."]
            }
        ]
    },
    {
        n: 18,
        title: "Security & Audits",
        blocks: [
            {
                q: "Has the code been audited?",
                a: [
                    "External security consultants have been involved since design inception and have advised the development team throughout. The whole protocol ‘Secure Safe’ concept was designed so that 80% of the initial deposit is contractually segregated from Treasury and market operations, this is the most secure solution we could devise.",
                    "Since the protocol does not have access to the Safe, even if it is compromised, there should be no exposure to an individuals Safe funds.",
                    "A narrow-scope audit of the Safe Vault and bond routing logic is planned.",
                    "Full audits will follow once TVL justifies the cost. A public bug-bounty program will also be added."
                ]
            },
            {
                q: "What happens if there is a bug?",
                a: ["Emergency pause functions exist, but the Safe Vault is structurally isolated and cannot be drained by the protocol even in a worst-case scenario."]
            }
        ]
    },
    {
        n: 19,
        title: "Advanced / Technical Questions",
        blocks: [
            {
                q: "Can I interact directly with the contracts without the frontend?",
                a: ["Yes. The protocol is fully permissionless. Advanced users can call the contracts directly via Etherscan or a block explorer."]
            },
            {
                q: "Is the protocol upgradeable?",
                a: ["Only non-core parameters are upgradeable via proxy. The Safe Vault, routing logic, and yield calculation are immutable."]
            }
        ]
    },
    {
        n: 20,
        title: "Getting Started & Practical Questions",
        blocks: [
            {
                q: "How do I start?",
                a: ["Connect a wallet, deposit USDC, choose standard or Turbo, and optionally enable Auto-Compound. Make sure you are on Base Blockchain and have the correct USDC."]
            },
            { q: "What wallet do I need?", a: ["Any wallet that works on Base (MetaMask, Rabby, WalletConnect, etc.)."] },
            {
                q: "What is my tax liability on the yield?",
                a: ["That is out of scope for this FAQ. You are encouraged to seek professional help from a tax expert in your own jurisdiction."]
            }
        ]
    }
];

/**
 * Renders rich text while preserving simple **bold** formatting and
 * automatically converting plain email addresses into clickable `mailto:`
 * links. The parser stays lightweight by splitting on `**` markers and
 * detecting email patterns without any external dependencies.
 */

function RichText({ text }) {
    const renderToken = (token, key) => {
        // Markdown Link
        const markdownMatch = token.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (markdownMatch) {
            const [, label, href] = markdownMatch;
            const [url, target] = href.split("|");
            const isExternal = /^https?:\/\//i.test(url);
            // Internal routes (e.g. /manifesto, /dashboard) navigate within
            // the SPA via react-router — no full-page reload, and no new tab
            // where a wallet-gated page would just bounce. External links keep
            // their |blank new-tab behaviour.
            if (!isExternal) {
                return (
                    <Link
                        key={key}
                        to={url}
                        className="text-blue-400 underline hover:text-blue-300"
                    >
                        {label}
                    </Link>
                );
            }
            const isBlank = target === "blank";
            return (
                <a
                    key={key}
                    href={url}
                    target={isBlank ? "_blank" : "_self"}
                    rel={isBlank ? "noopener noreferrer" : undefined}
                    className="text-blue-400 underline hover:text-blue-300"
                >
                    {label}
                </a>
            );
        }

        // Email
        if (config.EMAIL_REGEX.test(token)) {
            return (
                <a
                    key={key}
                    href={`mailto:${token}`}
                    className="text-blue-400 underline hover:text-blue-300"
                >
                    {token}
                </a>
            );
        }
        return <Fragment key={key}>{token}</Fragment>;
    };

    const renderContent = (content) => {
        const parts = content.split(config.TOKEN_REGEX);

        return parts.map((part, index) => {
            if (!part) return null;
            return renderToken(part, index);
        });
    };

    const boldParts = text.split("**");

    return (
        <>
            {boldParts.map((part, index) =>
                index % 2 === 1 ? (
                    <strong key={index} className="font-bold text-white">
                        {renderContent(part)}
                    </strong>
                ) : (
                    <Fragment key={index}>
                        {renderContent(part)}
                    </Fragment>
                )
            )}
        </>
    );
}

/** One infographic, rendered edge-to-edge inside a bordered gold frame. */
function FaqImage({ src, alt }) {
    return (
        <figure className="my-5 overflow-hidden rounded-[12px] ring-1 ring-[#bfb67b]/60 bg-[#0b1c12]">
            <img src={src} alt={alt} loading="lazy" className="block w-full h-auto" />
        </figure>
    );
}

/** A data-driven table (horizontally scrollable on small screens). */
function FaqTable({ head, rows }) {
    return (
        <div className="my-4 overflow-x-auto rounded-[10px] ring-1 ring-[#bfb67b]/50">
            <table className="w-full border-collapse text-left text-[12px]">
                <thead>
                    <tr className="bg-[#0e2417]">
                        {head.map((h, i) => (
                            <th
                                key={i}
                                className="border-b border-[#bfb67b]/40 px-3 py-2 font-manrope font-bold text-[#e6c985] align-top"
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, r) => (
                        <tr key={r} className={r % 2 ? "bg-white/[0.03]" : ""}>
                            {row.map((cell, c) => (
                                <td
                                    key={c}
                                    className={`border-b border-white/10 px-3 py-2 align-top ${
                                        c === 0 ? "font-semibold text-white" : "text-[#e6c985]/90"
                                    }`}
                                >
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** Renders one block inside an answer (paragraph, list, table or image). */
function AnswerBlock({ block }) {
    if (typeof block === "string") {
        return (
            <p className="mt-2.5 first:mt-0 text-[13px] leading-relaxed text-[#e6c985]/85">
                <RichText text={block} />
            </p>
        );
    }
    if (block.list) {
        return (
            <ul className="mt-2.5 space-y-1.5 pl-1">
                {block.list.map((li, i) => (
                    <li key={i} className="flex gap-2 text-[13px] leading-relaxed text-[#e6c985]/85">
                        <span className="mt-[7px] inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#FBC206]" aria-hidden="true" />
                        <span>
                            <RichText text={li} />
                        </span>
                    </li>
                ))}
            </ul>
        );
    }
    if (block.table) {
        return <FaqTable head={block.table.head} rows={block.table.rows} />;
    }
    if (block.image) {
        return <FaqImage src={block.image.src} alt={block.image.alt} />;
    }
    return null;
}

/**
 * A single Q&A entry rendered as a collapsible accordion: the question is
 * a toggle button and the answer (plus any inline infographic) expands
 * below it. Collapsed by default so a section reads as a scannable list of
 * questions; `open` is lifted to the parent section so "Expand all" /
 * "Collapse all" can drive every entry at once.
 */
function QaEntry({ item, open, onToggle }) {
    return (
        <div className="border-t border-white/10 pt-4 first:border-0 first:pt-0">
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={open}
                className="flex w-full items-start justify-between gap-3 text-left"
            >
                <h3 className="font-manrope text-[15px] font-bold leading-snug text-white">
                    <span className="text-[#FBC206]">Q: </span>
                    {item.q}
                </h3>
                <span
                    className={`mt-0.5 shrink-0 text-[#FBC206] transition-transform ${
                        open ? "rotate-45" : ""
                    }`}
                >
                    <PlusIcon />
                </span>
            </button>
            {open ? (
                <div className="mt-3">
                    {item.img ? <FaqImage src={item.img.src} alt={item.img.alt} /> : null}
                    <div className={item.img ? "" : "mt-0"}>
                        {item.a.map((block, i) => (
                            <AnswerBlock key={i} block={block} />
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function PlusIcon() {
    return (
        <svg
            className="h-4 w-4"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            aria-hidden="true"
        >
            <line x1="10" y1="4" x2="10" y2="16" />
            <line x1="4" y1="10" x2="16" y2="10" />
        </svg>
    );
}

/**
 * Floating "Back to top" control. Appears once the reader has scrolled a
 * screenful down and smooth-scrolls to the top of the document on click.
 */
function BackToTop() {
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 600);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!visible) return null;
    return (
        <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Back to top"
            className="fixed bottom-6 right-6 z-50 inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#FBC206] text-[#10281b] shadow-lg ring-1 ring-[#10281b]/20 transition-transform hover:-translate-y-0.5 hover:bg-[#ffce33]"
        >
            <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <polyline points="5 12 10 7 15 12" />
            </svg>
        </button>
    );
}

/**
 * Comprehensive FAQ page. Reproduces the source PDF as a readable
 * document: a dark-green surface matching the app chrome, gold accents,
 * a section table of contents, and every section rendered in order with
 * its infographics inline.
 */
export default function Faq() {
    return (
        // FAQ always renders on the full-bleed public chrome (no sidebar),
        // whose layout background is mint green (bg-vb-bg) — a shade the
        // client has ruled out. Paint a full-width cream backdrop (the same
        // #f6f6ec the public Protocol Health page uses) so the mint never
        // shows through the page margins, then centre the content inside.
        <div className="min-h-screen w-full bg-[#f6f6ec]">
            <div className="mx-auto flex w-full max-w-[1000px] flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
                {/* Header */}
                <header className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-6 py-7 text-center">
                    <p className="font-manrope text-[11px] font-bold uppercase tracking-[0.3em] text-[#FBC206]">
                        GILDer Protocol
                    </p>
                    <h1 className="mt-2 font-serif text-[30px] font-semibold leading-tight text-white sm:text-[38px]">
                        Comprehensive FAQ
                    </h1>
                </header>

                <FaqContent />
            </div>

            <BackToTop />
        </div>
    );
}

/**
 * The FAQ body — contents nav + every section — with NO page chrome, so
 * it can be mounted both on the standalone /faq route and inline on
 * /support (§M6 client review: "can we just have the FAQ embedded in this
 * page so they don't have to click out of it?").
 *
 * `collapsedByDefault` starts the whole block folded behind a single
 * disclosure; the /support surface uses it so the FAQ doesn't push the
 * parameter/contract cards off screen.
 */
export function FaqContent({ collapsedByDefault = false }) {
    const [shown, setShown] = useState(!collapsedByDefault);
    // Which Q&A entries are expanded, keyed "sectionN-blockIndex". All
    // collapsed initially so each section reads as a scannable question
    // list; the header "Expand all" toggle flips every entry at once.
    const [openMap, setOpenMap] = useState({});

    const allKeys = FAQ_SECTIONS.flatMap((s) =>
        s.blocks.map((b, i) => (b.q ? `${s.n}-${i}` : null)).filter(Boolean)
    );
    const allOpen = allKeys.every((k) => openMap[k]);

    function toggle(key) {
        setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
    }
    function toggleAll() {
        setOpenMap(allOpen ? {} : Object.fromEntries(allKeys.map((k) => [k, true])));
    }

    if (!shown) {
        return (
            <button
                type="button"
                onClick={() => setShown(true)}
                className="flex w-full items-center justify-between gap-3 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] px-5 py-4 text-left transition-colors hover:bg-[#12301f]"
            >
                <span>
                    <span className="block font-manrope text-[12px] font-bold uppercase tracking-wider text-[#e6c985]">
                        Comprehensive FAQ
                    </span>
                    <span className="mt-0.5 block text-[10.5px] text-[#e6c985]/70">
                        All {FAQ_SECTIONS.length} sections, right here — no need to leave this page.
                    </span>
                </span>
                <span className="shrink-0 rounded-md bg-[#FBC206] px-3 py-1 text-[11px] font-bold text-[#10281b]">
                    Show FAQ
                </span>
            </button>
        );
    }

    return (
        <div className="flex w-full flex-col gap-6">
            {/* Table of contents */}
            <nav
                aria-label="FAQ sections"
                className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-5"
            >
                <div className="flex items-center justify-between gap-3">
                    <h2 className="font-manrope text-[12px] font-bold uppercase tracking-wider text-[#e6c985]">
                        Contents
                    </h2>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="rounded-md bg-white/10 px-3 py-1 font-manrope text-[11px] font-bold text-[#FBC206] ring-1 ring-[#bfb67b]/60 hover:bg-white/15 transition-colors"
                        >
                            {allOpen ? "Collapse all" : "Expand all"}
                        </button>
                        {collapsedByDefault ? (
                            <button
                                type="button"
                                onClick={() => setShown(false)}
                                className="rounded-md bg-white/10 px-3 py-1 font-manrope text-[11px] font-bold text-[#e6c985] ring-1 ring-[#bfb67b]/60 hover:bg-white/15 transition-colors"
                            >
                                Hide FAQ
                            </button>
                        ) : null}
                    </div>
                </div>
                <ol className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                    {FAQ_SECTIONS.map((s) => (
                        <li key={s.n}>
                            <a
                                href={`#faq-section-${s.n}`}
                                className="group flex items-baseline gap-2 text-[12.5px] text-[#e6c985]/85 hover:text-[#FBC206] transition-colors"
                            >
                                <span className="font-bold text-[#FBC206]">{s.n}.</span>
                                <span className="group-hover:underline">{s.title}</span>
                            </a>
                        </li>
                    ))}
                </ol>
            </nav>

            {/* Sections */}
            {FAQ_SECTIONS.map((section) => (
                <section
                    key={section.n}
                    id={`faq-section-${section.n}`}
                    className="scroll-mt-24 rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-5 sm:p-6"
                >
                    <h2 className="mb-5 flex items-center gap-3 font-serif text-[22px] font-semibold text-[#e6c985]">
                        <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-[#FBC206] px-2 text-[14px] font-bold text-[#10281b]">
                            {section.n}
                        </span>
                        {section.title}
                    </h2>

                    <div className="flex flex-col gap-5">
                        {section.blocks.map((block, i) =>
                            block.image ? (
                                <FaqImage key={i} src={block.image.src} alt={block.image.alt} />
                            ) : (
                                <QaEntry
                                    key={i}
                                    item={block}
                                    open={Boolean(openMap[`${section.n}-${i}`])}
                                    onToggle={() => toggle(`${section.n}-${i}`)}
                                />
                            )
                        )}
                    </div>
                </section>
            ))}
        </div>
    );
}
